import { createFileRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listAllPosts, deletePost, checkAdmin } from "@/lib/blog.functions";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Admin — thatKorner" }, { name: "robots", content: "noindex" }] }),
  component: AdminLayout,
});

function AdminLayout() {
  const matches = useMatches();
  const isIndex = matches[matches.length - 1]?.routeId === "/_authenticated/admin";
  const navigate = useNavigate();
  const fetchCheck = useServerFn(checkAdmin);
  const { data: adminCheck, isLoading: checking } = useQuery({
    queryKey: ["admin-check"],
    queryFn: () => fetchCheck(),
  });

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  if (checking) {
    return <div className="bg-ink py-32 text-center text-bone/60">Checking access…</div>;
  }
  if (!adminCheck?.isAdmin) {
    return (
      <div className="bg-ink py-32 text-center text-bone">
        <h1 className="h-display text-3xl">Not an admin</h1>
        <p className="mt-3 text-bone/60">Your account doesn't have admin access.</p>
        <button onClick={signOut} className="mt-6 text-ember underline">
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="bg-ink pb-24 pt-28 text-bone">
      <div className="mx-auto max-w-content px-gutter">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-bone/10 pb-6">
          <div>
            <p className="font-script text-2xl text-ember">admin</p>
            <h1 className="h-display text-3xl">Blog Manager</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/posts/new"
              className="rounded-full bg-ember px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.2em] text-bone hover:bg-ember/90"
            >
              + New Post
            </Link>
            <button
              onClick={signOut}
              className="rounded-full border border-bone/20 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-bone/70 hover:border-bone/50"
            >
              Sign out
            </button>
          </div>
        </header>
        {isIndex ? <PostsList /> : <Outlet />}
      </div>
    </div>
  );
}

function PostsList() {
  const fetchPosts = useServerFn(listAllPosts);
  const del = useServerFn(deletePost);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "posts"],
    queryFn: () => fetchPosts(),
  });

  const mutation = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "posts"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
    },
  });

  return (
    <div className="mt-8">
      {isLoading ? (
        <p className="text-bone/70">Loading…</p>
      ) : (data?.posts ?? []).length === 0 ? (
        <p className="text-bone/60">No posts yet. Create your first one.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-bone/10">
          <table className="w-full text-sm">
            <thead className="bg-bone/5 text-left text-xs uppercase tracking-[0.15em] text-bone/60">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data!.posts.map((p: any) => (
                <tr key={p.id} className="border-t border-bone/10">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-bone/70">/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${
                        p.status === "published"
                          ? "bg-ember/15 text-ember"
                          : "bg-bone/10 text-bone/60"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-bone/60">
                    {new Date(p.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to="/admin/posts/$id"
                      params={{ id: p.id }}
                      className="text-xs text-ember hover:underline"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${p.title}"?`)) mutation.mutate(p.id);
                      }}
                      className="ml-4 text-xs text-red-400 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
